/**
 * BufferHistory - Version history list for the active buffer
 *
 * Shows commits with messages and timestamps.
 * Supports rollback and branch creation from any version.
 */

import { useState, useCallback } from 'react';
import { useBufferSync } from '../../contexts/BufferSyncContext';

export function BufferHistory() {
  const {
    history,
    activeBufferName,
    rollbackBuffer,
    branchBuffer,
  } = useBufferSync();

  const [isExpanded, setIsExpanded] = useState(false);
  const [branchInput, setBranchInput] = useState<string | null>(null);
  const [branchName, setBranchName] = useState('');

  const handleRollback = useCallback(async (steps: number) => {
    await rollbackBuffer(activeBufferName, steps);
  }, [rollbackBuffer, activeBufferName]);

  const handleCreateBranch = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!branchName.trim()) return;
    await branchBuffer(activeBufferName, branchName.trim());
    setBranchName('');
    setBranchInput(null);
  }, [branchBuffer, activeBufferName, branchName]);

  if (history.length === 0) return null;

  return (
    <div className="buffer-history">
      <button
        className="buffer-history__toggle"
        onClick={() => setIsExpanded((v) => !v)}
      >
        <span className="buffer-history__label">History</span>
        <span className="buffer-history__count">{history.length}</span>
        <span className="buffer-history__caret">{isExpanded ? '\u25B4' : '\u25BE'}</span>
      </button>

      {isExpanded && (
        <div className="buffer-history__list">
          {history.map((version, idx) => (
            <div key={version.id} className="buffer-history__item">
              <div className="buffer-history__item-info">
                <span className="buffer-history__message">
                  {version.message || 'Unnamed commit'}
                </span>
                <span className="buffer-history__time">
                  {formatTime(version.timestamp)}
                </span>
              </div>
              <div className="buffer-history__item-actions">
                {idx > 0 && (
                  <button
                    className="buffer-history__action"
                    onClick={() => handleRollback(idx)}
                    title={`Rollback ${idx} step${idx > 1 ? 's' : ''}`}
                  >
                    Restore
                  </button>
                )}
                <button
                  className="buffer-history__action"
                  onClick={() => setBranchInput(version.id)}
                  title="Create branch from this version"
                >
                  Branch
                </button>
              </div>
              {branchInput === version.id && (
                <form className="buffer-history__branch-form" onSubmit={handleCreateBranch}>
                  <input
                    className="buffer-history__branch-input"
                    type="text"
                    placeholder="Branch name..."
                    value={branchName}
                    onChange={(e) => setBranchName(e.target.value)}
                    autoFocus
                  />
                  <button className="buffer-history__branch-submit" type="submit">Go</button>
                  <button
                    className="buffer-history__branch-cancel"
                    type="button"
                    onClick={() => { setBranchInput(null); setBranchName(''); }}
                  >
                    Cancel
                  </button>
                </form>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  const now = Date.now();
  const diff = now - ts;

  if (diff < 60_000) return 'just now';
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86400_000) return `${Math.floor(diff / 3600_000)}h ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
